import { SearchBoxSuggestion } from '@mapbox/search-js-core';
import { useSearchBoxCore, useSearchSession } from '@mapbox/search-js-react';
import { useOrganizationWidePreference } from 'kineticUtils/hooks';
import { LngLatLike } from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import { useViewState } from '../../contexts/ViewState';
import { MapAccessTokenData } from '../types';
import { debounce } from 'lodash';

export const useMapboxSuggestions = ({ searchText }: { searchText: string }) => {
  const [suggestions, setSuggestions] = useState<SearchBoxSuggestion[]>([]);
  const viewState = useViewState();

  const searchOptions = useMemo(() => {
    const proximity: LngLatLike = { lng: viewState.longitude ?? 0, lat: viewState.latitude ?? 0 };
    return { limit: 5, proximity };
  }, [viewState.latitude, viewState.longitude]);

  const { value: systemPreference } = useOrganizationWidePreference<MapAccessTokenData>({
    key: PREFERENCE_KEYS.MAP_ACCESS_TOKEN,
  });

  const accessToken = useMemo(
    () => systemPreference?.mapAccessToken ?? '',
    [systemPreference?.mapAccessToken]
  );

  const searchCore = useSearchBoxCore({
    accessToken,
  });

  const searchSession = useSearchSession(searchCore);

  const onSuggest = useCallback((res) => {
    setSuggestions(res?.suggestions ?? []);
  }, []);

  useEffect(() => {
    searchSession.addEventListener('suggest', onSuggest);
    return () => {
      searchSession.removeEventListener('suggest', onSuggest);
    };
    // only do this on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSuggestions = useMemo(
    () => debounce((text, options) => searchSession.suggest(text, options), 150),
    [searchSession]
  );

  useEffect(() => {
    getSuggestions(searchText, searchOptions);
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return { suggestions, searchSession };
};
